import { create } from 'zustand'
import { del, get, set } from 'idb-keyval'
import { enqueueChangeset, type Changeset, type RecordRef, type SyncEntity } from '@mads/shared'
import { pruneQueuedPhotos } from './photoStore'

/**
 * The queue of changes made with no connection (issue #64, step 2).
 *
 * It sits *under* the API client, not inside the screens: a mutation that is
 * offline is enqueued here and reported to the caller as applied, and the read
 * caches are patched as if it had been. That is the whole reason `queries.ts`
 * holds every mutation in one file — the queueing decision is made in one place
 * and `Inventory.tsx` never learns there is a queue at all.
 *
 * **Stored against the user id that made the changes.** `JWT_TTL` is twelve
 * hours and there is no refresh token, so a device offline longer than that
 * comes back holding a queue and a dead token; the queue therefore has to
 * survive signing out and back in. It must equally never be replayed under
 * somebody else's name, which is why the account is part of the key rather than
 * something checked at sync time and hoped for.
 */
const KEY_PREFIX = 'mads.queue'

function keyFor(userId: number): string {
  return `${KEY_PREFIX}.${userId}`
}

function deferralKeyFor(userId: number): string {
  return `${KEY_PREFIX}.deferred.${userId}`
}

function sentKeyFor(userId: number): string {
  return `${KEY_PREFIX}.sent.${userId}`
}

interface QueueState {
  /** Whose queue is loaded. Null before a session is known. */
  userId: number | null
  changesets: Changeset[]
  /**
   * Changesets the walker has been told about once and asked to keep for later
   * (issue #64, step 3).
   *
   * There is exactly one refusal this applies to — a walk meeting another one
   * already open — and it is the only refusal in the queue that **lifts by
   * itself**, because somebody else is going to finish theirs. So the change
   * goes on being sent every time, and this only records that the walker has
   * already had the conversation: hearing the same answer again does not block
   * the app a second time. Persisted, because being asked afresh on every
   * reload is the same nuisance by another route.
   *
   * A deferral is forgotten the moment its changeset applies or is discarded.
   */
  deferred: string[]
  /**
   * Changesets that have been sent to the server at least once (issue #70).
   *
   * Nothing in here is ever folded into by a later change, and the reason is
   * narrow but real: `SyncApplier` answers a repeat of an *applied* changeset id
   * out of `sync_record` without running it, which is what makes a batch whose
   * answer was lost in a stairwell survivable. Merge a new field into such an id
   * and the server would hand back the old result and the new edit would vanish
   * without a word. So a sent changeset is left alone and the change is appended
   * behind it — which is what the queue did for everything before coalescing.
   *
   * Persisted beside `deferred`, and for the same reason: a reload must not turn
   * a sent changeset back into a mergeable one.
   */
  sent: string[]
  /** True until the stored queue has been read back. */
  loading: boolean
}

interface QueueActions {
  load: (userId: number) => Promise<void>
  add: (changeset: Changeset) => Promise<void>
  /**
   * Replaces the whole queue — what a sync answer produces, having dropped
   * everything that applied and rewritten what is still to go.
   */
  replace: (changesets: Changeset[]) => Promise<void>
  /** Forgets one change, which is what discarding it in the resolver does. */
  discard: (changesetId: string) => Promise<void>
  /**
   * Forgets a walk and everything queued about it — its start, its counts and
   * its finish. Discarding only the start would leave counts pointing at a
   * record that will never exist, which is a queue that can never empty.
   */
  discardWalk: (clientId: string) => Promise<void>
  /** Remembers that the walker has already been asked about this one. */
  defer: (changesetId: string) => Promise<void>
  /**
   * Records that these changesets have gone to the server, whatever came back.
   * Called before the send rather than after it, because a request that failed
   * on the way *back* is indistinguishable from one that never arrived — and it
   * is precisely the first of those this guards against.
   */
  markSent: (changesetIds: readonly string[]) => Promise<void>
  clear: () => Promise<void>
}

export const useQueueStore = create<QueueState & QueueActions>()((setState, getState) => ({
  userId: null,
  changesets: [],
  deferred: [],
  sent: [],
  loading: true,

  load: async (userId) => {
    const [stored, deferred, sent] = await Promise.all([
      get<Changeset[]>(keyFor(userId)),
      get<string[]>(deferralKeyFor(userId)),
      get<string[]>(sentKeyFor(userId)),
    ])

    setState({
      userId,
      changesets: stored ?? [],
      deferred: deferred ?? [],
      sent: sent ?? [],
      loading: false,
    })
  },

  /**
   * Adds a change, folding it into one already queued about the same record
   * where that is the same change said again (issue #70) — `enqueueChangeset`
   * in `@mads/shared` holds the whole of that decision, so React Native gets it
   * with the queue.
   */
  add: async (changeset) => {
    const { userId, changesets, sent } = getState()

    if (userId === null) return

    const next = enqueueChangeset(changesets, changeset, new Set(sent))

    setState({ changesets: next })
    await set(keyFor(userId), next)

    // Folding can drop a queued photograph — a walker photographing the same
    // lantern twice — and this is a rewrite of the queue, which is the only
    // moment a blob becomes an orphan.
    await pruneQueuedPhotos(userId, new Set(next.map((queued) => queued.id)))
  },

  replace: async (changesets) => {
    const { userId, deferred, sent } = getState()

    if (userId === null) return

    // A deferral outliving the change it was about would silently suppress the
    // block for a changeset id that could, in principle, be minted again. The
    // sent set is pruned the same way and for a plainer reason: it is only ever
    // consulted about changesets that are still queued.
    const alive = new Set(changesets.map((changeset) => changeset.id))
    const stillDeferred = deferred.filter((id) => alive.has(id))
    const stillSent = sent.filter((id) => alive.has(id))

    setState({ changesets, deferred: stillDeferred, sent: stillSent })

    // A photograph's bytes live under their own key (issue #64, step 4), so
    // this is the only moment one can become an orphan — its changeset applied,
    // or somebody discarded it. Nothing else ever looks at those keys, so an
    // orphan left here is invisible and permanent.
    await pruneQueuedPhotos(userId, alive)

    await (stillDeferred.length === 0
      ? del(deferralKeyFor(userId))
      : set(deferralKeyFor(userId), stillDeferred))

    await (stillSent.length === 0 ? del(sentKeyFor(userId)) : set(sentKeyFor(userId), stillSent))

    if (changesets.length === 0) {
      await del(keyFor(userId))
      return
    }

    await set(keyFor(userId), changesets)
  },

  discard: async (changesetId) => {
    const { changesets, replace } = getState()

    await replace(changesets.filter((queued) => queued.id !== changesetId))
  },

  discardWalk: async (clientId) => {
    const { changesets, replace } = getState()

    await replace(changesets.filter((queued) => !aboutWalk(queued, clientId)))
  },

  defer: async (changesetId) => {
    const { userId, deferred } = getState()

    if (userId === null || deferred.includes(changesetId)) return

    const next = [...deferred, changesetId]

    setState({ deferred: next })
    await set(deferralKeyFor(userId), next)
  },

  markSent: async (changesetIds) => {
    const { userId, sent } = getState()

    if (userId === null || changesetIds.length === 0) return

    const next = [...new Set([...sent, ...changesetIds])]

    if (next.length === sent.length) return

    setState({ sent: next })
    await set(sentKeyFor(userId), next)
  },

  clear: async () => {
    await getState().replace([])
  },
}))

/** Whether a queued change is the start of this walk, or belongs to it. */
function aboutWalk(changeset: Changeset, clientId: string): boolean {
  if (changeset.entity !== 'stocktake') return false

  if (changeset.kind === 'create') return changeset.clientId === clientId

  return 'clientId' in changeset.target && changeset.target.clientId === clientId
}

/** How many changes are waiting — what the shell shows the whole time. */
export function useQueueDepth(): number {
  return useQueueStore((s) => s.changesets.length)
}

/**
 * Points a change at a record, by whichever name it has.
 *
 * A record created offline has no server id, so anything that follows it —
 * a later edit, an item put on the shelf created five minutes ago — names it by
 * the client id instead, and the server maps the two during replay.
 */
export function refFor(id: number | string): RecordRef {
  return typeof id === 'number' ? { id } : { clientId: id }
}

/**
 * Rewrites a queue in the light of what a sync answered (issue #64).
 *
 * Applied changesets go. Everything else stays, because none of the other three
 * answers is final: a conflict is waiting for the walker, a block is waiting for
 * its dependency, and a rejection has to be *seen* before it is thrown away —
 * discarding somebody's count without showing it to them is exactly what this
 * design exists to prevent.
 */
export function withoutApplied(
  changesets: readonly Changeset[],
  appliedIds: ReadonlySet<string>,
): Changeset[] {
  return changesets.filter((changeset) => !appliedIds.has(changeset.id))
}

/**
 * The entity a queued change is about, for grouping in the resolver — where
 * conflicts are walked one record at a time.
 */
export function entityOf(changeset: Changeset): SyncEntity {
  return changeset.entity
}
